import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, Loader2, Zap, Brain, Database, CreditCard, Check } from "lucide-react";

const SAAS_TYPES = ["B2B SaaS", "Marketplace", "Outil IA", "CRM", "Micro-SaaS", "E-commerce"];
const STACKS = [
  "React + FastAPI + MongoDB + Stripe",
  "Next.js + Supabase + Stripe",
  "Vue + Node.js + PostgreSQL + Stripe",
];

const EXAMPLES = [
  "Une plateforme pour freelances qui automatise l'envoi de factures et les relances par email.",
  "Un outil qui analyse les avis clients et génère des rapports de sentiment mensuels.",
  "Un CRM léger dédié aux consultants indépendants avec suivi de deals et facturation.",
];

const STEPS = [
  { icon: Brain, label: "Analyse du concept" },
  { icon: Sparkles, label: "Génération du design" },
  { icon: Database, label: "Schéma de base de données" },
  { icon: CreditCard, label: "Modèle économique" },
  { icon: Zap, label: "Finalisation du blueprint" },
];

export default function GeneratorPage() {
  const [description, setDescription] = useState("");
  const [saasType, setSaasType] = useState(SAAS_TYPES[0]);
  const [stack, setStack] = useState(STACKS[0]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const { refresh, quota } = useAuth();
  const navigate = useNavigate();

  const quotaFull = quota && quota.used >= quota.quota;

  const runStepAnimation = () => {
    let i = 0;
    const interval = setInterval(() => {
      setActiveStep(i);
      i++;
      if (i >= STEPS.length) clearInterval(interval);
    }, 1200);
    return interval;
  };

  const generate = async () => {
    if (description.trim().length < 15) {
      toast.error("Décrivez votre SaaS avec un peu plus de détails (min. 15 caractères)");
      return;
    }
    setLoading(true);
    setActiveStep(0);
    const stepInterval = runStepAnimation();
    try {
      const { data } = await api.post("/generate", {
        description,
        saas_type: saasType,
        stack,
      });
      clearInterval(stepInterval);
      setActiveStep(STEPS.length);
      await refresh();
      toast.success("Blueprint généré !");
      setTimeout(() => navigate(`/projet/${data.id}`), 400);
    } catch (err) {
      clearInterval(stepInterval);
      setActiveStep(-1);
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="generator-main">
        <div className="animate-fade-up">
          <div className="text-xs font-mono uppercase tracking-widest text-indigo-400">Générateur IA</div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2">Décrivez votre SaaS</h1>
          <p className="text-gray-400 mt-2">L'IA génère design, base de données, endpoints et modèle économique en quelques secondes.</p>
        </div>

        {quotaFull && (
          <div className="mt-8 rounded-2xl p-5 bg-amber-500/10 border border-amber-500/30 flex items-center justify-between" data-testid="quota-full-banner">
            <div>
              <div className="font-semibold text-amber-300">Quota mensuel atteint</div>
              <div className="text-sm text-amber-200/70">Passez à un forfait supérieur pour continuer.</div>
            </div>
            <button onClick={() => navigate("/tarifs")} className="pill-primary text-sm">Voir les tarifs</button>
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="glass rounded-2xl p-6">
              <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Décrivez votre idée</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={7}
                disabled={loading}
                className="mt-3 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 focus:bg-white/[0.07] transition-colors resize-none text-base"
                placeholder="Ex : Un outil qui aide les coachs sportifs à gérer leurs clients, envoyer des programmes personnalisés et facturer automatiquement."
                data-testid="generator-description-input"
              />

              <div className="mt-4 flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => setDescription(ex)}
                    disabled={loading}
                    className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-indigo-500/40 hover:bg-indigo-500/10 text-gray-400 hover:text-white transition-colors"
                    data-testid={`example-prompt-${i}`}
                  >
                    Exemple {i + 1}
                  </button>
                ))}
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Type de SaaS</label>
                  <select
                    value={saasType}
                    onChange={(e) => setSaasType(e.target.value)}
                    disabled={loading}
                    className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 transition-colors"
                    data-testid="generator-type-select"
                  >
                    {SAAS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Stack technique</label>
                  <select
                    value={stack}
                    onChange={(e) => setStack(e.target.value)}
                    disabled={loading}
                    className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 transition-colors"
                    data-testid="generator-stack-select"
                  >
                    {STACKS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <button
                onClick={generate}
                disabled={loading || quotaFull}
                className="mt-8 pill-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="generator-submit-button"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                {loading ? "Génération en cours..." : "Générer mon SaaS"}
              </button>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="glass rounded-2xl p-6 sticky top-24">
              <div className="text-xs font-mono uppercase tracking-widest text-cyan-400">Processus IA</div>
              <h3 className="mt-2 text-lg font-semibold">Génération en 5 étapes</h3>
              <div className="mt-6 space-y-3" data-testid="steps-panel">
                {STEPS.map((s, i) => {
                  const done = activeStep > i;
                  const active = activeStep === i;
                  return (
                    <div
                      key={s.label}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-300 ${done ? 'border-emerald-500/30 bg-emerald-500/5' : active ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-white/5 bg-white/[0.02]'}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${done ? 'bg-emerald-500/20' : active ? 'bg-indigo-500/20 animate-pulse-glow' : 'bg-white/5'}`}>
                        {done ? <Check size={14} className="text-emerald-400" /> : <s.icon size={14} className={active ? 'text-indigo-300' : 'text-gray-500'} />}
                      </div>
                      <span className={`text-sm ${done ? 'text-emerald-200' : active ? 'text-white' : 'text-gray-500'}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 text-xs text-gray-500 font-mono">
                Propulsé par Claude Sonnet 5
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
